import React from "react";
import {
  Box,
  Container,
  Typography,
  Stack,
  Divider,
  useTheme,
} from "@mui/material";

const PrivacyPolicy: React.FC = () => {
  const theme = useTheme();

  const lastUpdated = "December 3, 2025";
  const contactEmail = "merraiofficial@gmail.com";

  const sections: Array<{
    title: string;
    content?: string;
    subsections?: Array<{ subtitle: string; content: string }>;
  }> = [
    {
      title: "1. Who We Are",
      content:
        "MeraAI (“we”, “us”, “our”) provides tools that help businesses automate conversations and workflows, including integrations with third-party platforms such as WhatsApp Business (Meta) and Shopify (together, the “Services”).",
    },
    {
      title: "2. Information We Collect",
      subsections: [
        {
          subtitle: "2.1 Information you provide",
          content:
            "We may collect information you choose to share, such as:\n• Account details (name, email, phone number, business details)\n• Support communications (messages you send to us)\n• Integration details needed to connect your accounts (e.g., Meta/WhatsApp permissions, Shopify store connection)\n• Billing information (payments are processed by third-party payment providers; we typically receive confirmation and limited billing metadata, not full card details)",
        },
        {
          subtitle: "2.2 Information collected automatically",
          content:
            "When you use the Services, we may collect:\n• Device and connection data (IP address, browser type, device type, operating system)\n• Usage data (pages viewed, features used, actions taken, timestamps)\n• Cookies and similar technologies (for login, preferences, analytics, and security)\n• Basic logs needed to keep the Services reliable and secure",
        },
        {
          subtitle: "2.3 Information from third parties",
          content:
            "If you connect third-party services, we may receive information from them as permitted by your settings and their policies (e.g., Meta/WhatsApp and Shopify).",
        },
      ],
    },
    {
      title: "3. How We Use Your Information",
      content:
        "We use information to:\n• Provide, operate, and maintain the Services\n• Create and manage accounts and integrations\n• Provide customer support and respond to requests\n• Send important notices (e.g., security alerts, service updates)\n• Monitor usage, troubleshoot issues, and improve performance\n• Prevent fraud, abuse, and unauthorized access\n• Comply with legal obligations and enforce our terms",
    },
    {
      title: "4. Legal Bases (Where Applicable)",
      content:
        "If your location requires a legal basis (e.g., GDPR/UK GDPR), we process information based on:\n• Contract (to provide the Services you request)\n• Legitimate interests (to secure, improve, and operate the Services)\n• Consent (where required, such as certain cookies/marketing)\n• Legal obligation (to comply with applicable laws)",
    },
    {
      title: "5. How We Share Information",
      content:
        "We may share information only as needed:\n• With service providers that help us run the Services (e.g., hosting, analytics, support tools, payment processors)\n• If required by law, legal process, or to protect rights, safety, and security\n• In connection with a merger, acquisition, financing, or sale of assets (with appropriate safeguards)\n\nWe do not sell your personal information.",
    },
    {
      title: "6. WhatsApp (Meta) Integration Privacy",
      content:
        "If you connect WhatsApp Business:\n• You authorize MeraAI to access and process data as needed to provide the integration\n• Meta’s terms and policies apply to your WhatsApp Business Account and related data\n• Message handling depends on your configuration; we may process message content to deliver features (automation, routing, analytics) and may store data where necessary to operate the Services\n• You can revoke access by disconnecting the integration or changing permissions in Meta settings (where available)",
    },
    {
      title: "7. Shopify Integration Privacy",
      content:
        "If you connect Shopify:\n• You authorize MeraAI to access and process store data as needed for enabled features\n• Shopify’s terms and policies apply to your Shopify account and store\n• We may process store data (such as products and orders) to provide automations you enable\n• You can revoke access by uninstalling/disconnecting MeraAI from your Shopify admin",
    },
    {
      title: "8. Cookies and Tracking",
      content:
        "We use cookies and similar technologies for:\n• Authentication and session management\n• Remembering preferences\n• Analytics and performance monitoring\n• Security\n\nYou can control cookies through your browser settings. Disabling cookies may affect certain functionality.",
    },
    {
      title: "9. Data Security",
      content:
        "We use reasonable administrative, technical, and organizational safeguards designed to protect your information (such as encryption in transit, access controls, and monitoring). However, no system is 100% secure, and we cannot guarantee absolute security.",
    },
    {
      title: "10. Data Retention",
      content:
        "We keep information only as long as needed for the purposes described in this policy, including:\n• While your account is active\n• For a limited period after deletion where needed for recovery, security, and compliance\n• Longer if required by law (e.g., tax/accounting) or to resolve disputes and enforce agreements",
    },
    {
      title: "11. Your Rights and Choices",
      content:
        "Depending on your location, you may have the right to:\n• Access, correct, or delete your personal information\n• Object to or request restriction of certain processing\n• Request data portability\n• Withdraw consent where processing is based on consent\n• Opt out of marketing messages\n\nTo request any of the above, email us at: " +
        contactEmail,
    },
    {
      title: "12. International Data Transfers",
      content:
        "Your information may be processed in countries other than where you live. Where required, we use appropriate safeguards for cross-border transfers.",
    },
    {
      title: "13. Children’s Privacy",
      content:
        "MeraAI is not intended for children under 13, and we do not knowingly collect information from children under 13.",
    },
    {
      title: "14. Third-Party Links",
      content:
        "Our Services may contain links to third-party websites or services. Their privacy practices are governed by their own policies, not this one.",
    },
    {
      title: "15. Changes to This Privacy Policy",
      content:
        "We may update this Privacy Policy from time to time. We will post the updated version on our website and may notify you of material changes via the Services or email. Continued use of the Services means you accept the updated policy.",
    },
    {
      title: "16. Contact Us",
      content:
        "For privacy questions or requests, contact:\n\nEmail: " + contactEmail,
    },
  ];

  return (
    <Box>
      <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6 } }}>
          {/* Header */}
          <Stack spacing={2} sx={{ mb: 4 }}>
            <Typography
              variant="h3"
              fontWeight={800}
              sx={{
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.success.main})`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Privacy Policy
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Last updated: {lastUpdated}
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
              This Privacy Policy explains how MeraAI collects, uses, shares,
              and protects information when you use our website and Services.
            </Typography>
          </Stack>

          <Divider sx={{ mb: 4 }} />

          {/* Content Sections */}
          <Stack spacing={4}>
            {sections.map((section, index) => (
              <Box key={index}>
                <Typography
                  variant="h5"
                  fontWeight={700}
                  sx={{ mb: 2, color: theme.palette.primary.main }}
                >
                  {section.title}
                </Typography>

                {section.content && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      whiteSpace: "pre-wrap",
                      lineHeight: 1.8,
                    }}
                  >
                    {section.content}
                  </Typography>
                )}

                {section.subsections && (
                  <Stack spacing={2}>
                    {section.subsections.map((subsection, subIndex) => (
                      <Box key={subIndex}>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{ mb: 1, color: theme.palette.text.primary }}
                        >
                          {subsection.subtitle}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            whiteSpace: "pre-wrap",
                            lineHeight: 1.8,
                          }}
                        >
                          {subsection.content}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 4 }} />

          {/* Footer Note */}
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: theme.palette.action.hover,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              If you have questions about this Privacy Policy, email us at{" "}
              {contactEmail}.
            </Typography>
          </Box>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;
